import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createSlots as _createSlots, createVNode as _createVNode, createBlock as _createBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "flex flex-wrap w-full mb-2 md:flex-nowrap align-items-center justify-content-end md:justify-content-between" }
const _hoisted_2 = {
  key: 0,
  class: "mr-auto caption"
}
const _hoisted_3 = { class: "flex w-full align-items-center justify-content-between" }

import { ethers } from 'ethers';
import BigNumber from 'bignumber.js';
import { useI18n } from 'vue-i18n';
import { computed, ref, watch, withDefaults } from 'vue';
import { useStore } from 'vuex';
import { BN_ZERO, NO_CONTRACT_ADDRESS } from '@/sdk/constants';
import { NATIVE_TOKEN_SYMBOL } from '@/helpers/constants';
import { DEFAULT_NETWORK_ID } from '@/helpers/networkParams.helper';
import { DEFAULT_CARDANO_CHAIN_ID } from '@/constants/DEFAULT_CARDANO_ID';
import { ENABLE_FAKE_CARDANO_NETWORK } from '@/helpers/fakeCardanoNetwork';
import { TokenAmount } from '@/sdk/entities/fractions/tokenAmount';
import { Token } from '@/sdk/entities/token';
import { Staking } from '@/sdk/entities/staking';
import { AutoStaking } from '@/sdk/entities/autoStaking';
import compareMixin from '@/mixins/compare.mixin';
import { fromWei, toWei } from '@/sdk/utils';
import {
  minDecimalsFromCardanoAndMilkomeda,
  normalizeWeiByMinDecimalsWhenExistCardanoToken,
} from '@/helpers/milkomeda-wrapped-smartcontract/milkomeda-wsc-calculation';
import BaseInputNumber from '@/components/form/BaseInputNumber.vue';
import InputBalance from '@/components/form/InputBalance.vue';
import TokenIcon from '@/components/token/TokenIcon.vue';
import { useTokens } from '@/store/modules/tokens/useTokens';
import { useBalances } from '@/store/modules/tokens/useBalances';
import { useMilkomedaWSCBridgeCalculations } from '@/composables/milkomeda-wrapped-smartcontract/useMilkomedaWSCBridgeCalculations';


export default /*#__PURE__*/_defineComponent({
  __name: 'InputPanel',
  props: {
    action: null,
    pool: null,
    poolType: null,
    disabled: { type: Boolean },
    modelValue: null,
    isMaxUnstakeMode: { type: Boolean, default: false },
    setMaxUnstakeMode: null
  },
  emits: ["hasValidationError", "update:modelValue"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'hasValidationError', value: { hasError: boolean; value: string }): void;
  (e: 'update:modelValue', value: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { action: 'stake' | 'unstake', pool: Staking | AutoStaking, poolType: string, disabled: boolean, modelValue: string, isMaxUnstakeMode: boolean, setMaxUnstakeMode?: (value: boolean) => void }

const { eq } = compareMixin.methods;

const { t } = useI18n();
const { state } = useStore();
const { isPresentTokenIntoNetwork, getTokenByAddressAndChainId } = useTokens();
const { balances, balanceByToken, balanceByTokenSymbolAndChainId } = useBalances();
const { estimateMaxBridgeAmountFromCardano } = useMilkomedaWSCBridgeCalculations();

const EMPTY_BALANCE = '0';





const internalValue = ref('');
const isBalanceError = ref(false);
const isPanelFocus = ref(false);
const maxAmount = ref(EMPTY_BALANCE);

const poolToken = computed(() => {
  if (!DEFAULT_NETWORK_ID) return;
  if (props.pool.token === ethers.constants.AddressZero) return null;

  return getTokenByAddressAndChainId(props.pool.token, +DEFAULT_NETWORK_ID);
});

const isPresentPoolTokenInCardano = computed(() => {
  return (
    poolToken.value &&
    isPresentTokenIntoNetwork(poolToken.value.symbol!, +DEFAULT_CARDANO_CHAIN_ID!)
  );
});

const maxFractionDigits = computed(() => {
  if (!poolToken.value) {
    return 2;
  }

  if (ENABLE_FAKE_CARDANO_NETWORK) {
    return minDecimalsFromCardanoAndMilkomeda(poolToken.value);
  }

  return poolToken.value.decimals;
});

const isUnstakeFromAutoPool = computed(() => {
  return props.action === 'unstake' && props.poolType === 'auto';
});

// STAKE BALANCES
const isBalanceTooltip = computed(() => {
  return (
    poolToken.value &&
    props.action === 'stake' &&
    ENABLE_FAKE_CARDANO_NETWORK &&
    isPresentTokenIntoNetwork(poolToken.value.symbol!, +DEFAULT_CARDANO_CHAIN_ID!)
  );
});
const stakeBalanceInCardano = computed(() => {
  if (!ENABLE_FAKE_CARDANO_NETWORK || !poolToken.value) {
    return EMPTY_BALANCE;
  }

  const { symbol } = poolToken.value;

  if (!isPresentPoolTokenInCardano.value) {
    return EMPTY_BALANCE;
  }

  const rawBalance = balanceByTokenSymbolAndChainId(symbol!, +DEFAULT_CARDANO_CHAIN_ID!).value
    ?.balance;
  return rawBalance?.toExact() || EMPTY_BALANCE;
});
const stakeBalanceInCardanoFixed = computed(() =>
  BigNumber(stakeBalanceInCardano.value).toFixed(2),
);
const stakeBalanceInMilkomeda = computed(() => {
  if (!poolToken.value) {
    return EMPTY_BALANCE;
  }

  const { symbol } = poolToken.value;

  const rawBalance = balanceByTokenSymbolAndChainId(symbol!, +DEFAULT_NETWORK_ID!).value?.balance;
  return rawBalance?.toExact() || EMPTY_BALANCE;
});
const stakeBalanceInMilkomedaFixed = computed(() =>
  BigNumber(stakeBalanceInMilkomeda.value).toFixed(2),
);
const stakeBalance = computed(() => {
  const balance = poolToken.value ? balanceByToken(poolToken.value).value?.balance : null;

  if (!balance) {
    console.warn(`[POOLS] Can not get balance for ${poolToken.value?.symbol}.`);
    return new TokenAmount(
      new Token(poolToken.value?.chainId ?? +DEFAULT_NETWORK_ID!, NO_CONTRACT_ADDRESS, 1, 'FAKE'),
      BN_ZERO,
    );
  }

  return balance;
});

// UNSTAKE BALANCES
const unstakeBalance = computed(() => {
  return props.poolType === 'manual'
    ? (props.pool as Staking).printYouStaked.toFixed()
    : (props.pool as AutoStaking).printAvailableCompound.toFixed();
});

function setPanelFocus(value) {
  isPanelFocus.value = value;
}

function getBalance() {
  const stakeBalanceInToken = stakeBalance.value.toExact();
  const unstakeBalanceInToken = unstakeBalance.value;

  return {
    value: props.action === 'stake' ? stakeBalanceInToken : unstakeBalanceInToken,
    symbol: poolToken.value?.symbol,
  };
}

function onChangeInput(value: string, isChangeFromMaxMode = false) {
  if (!isChangeFromMaxMode && isUnstakeFromAutoPool.value) {
    props.setMaxUnstakeMode?.(false);
  }

  internalValue.value = value;
  isBalanceError.value = false;
  let hasBalanceError = false;
  let hasValidationError = true;
  let hasDecimalsError = false;
  let returnValue;

  if (internalValue.value !== '') {
    returnValue = new BigNumber(internalValue.value);
    hasValidationError = returnValue.lte(0);
    isBalanceError.value = hasBalanceError = returnValue.gt(getBalance().value);
  }

  emits('hasValidationError', {
    hasError: hasValidationError || hasDecimalsError || hasBalanceError,
    value: hasDecimalsError ? BN_ZERO.toString() : returnValue?.toFixed(),
  });
  emits('update:modelValue', internalValue.value);
}

function onSetMax() {
  if (isUnstakeFromAutoPool.value) {
    props.setMaxUnstakeMode?.(true);
  }
  internalValue.value = maxAmount.value;
  onChangeInput(internalValue.value, true);
}

async function calculateMaxAmountForStake(): Promise<string> {
  const maxBalanceInMilkomeda = stakeBalanceInMilkomeda.value;

  if (!poolToken.value) {
    return maxBalanceInMilkomeda;
  }

  const maxBalanceInCardano = await estimateMaxBridgeAmountFromCardano(poolToken.value);

  const maxBalanceForStake = BigNumber(maxBalanceInCardano ?? EMPTY_BALANCE).plus(
    maxBalanceInMilkomeda,
  );

  return maxBalanceForStake.toFixed();
}

async function calculateMaxAmount() {
  let maxBalance = getBalance().value;

  if (ENABLE_FAKE_CARDANO_NETWORK && isPresentPoolTokenInCardano.value && poolToken.value) {
    if (props.action === 'stake') {
      maxBalance = await calculateMaxAmountForStake();
    }

    const maxBalanceByMinDecimalsInWei = normalizeWeiByMinDecimalsWhenExistCardanoToken(
      toWei(maxBalance, poolToken.value!.decimals),
      poolToken.value,
    );

    maxBalance = fromWei(maxBalanceByMinDecimalsInWei, poolToken.value.decimals).toFixed();
  }

  return maxBalance;
}

watch(
  () => props.action,
  () => {
    onChangeInput(internalValue.value);
  },
  {
    immediate: true,
  },
);

watch(
  () => props.modelValue,
  value => {
    onChangeInput(value, props.isMaxUnstakeMode);
  },
);

watch(
  () => balances?.[NATIVE_TOKEN_SYMBOL],
  () => {
    if (props.isMaxUnstakeMode && isUnstakeFromAutoPool.value) {
      onChangeInput(
        (props.pool as AutoStaking).printAvailableCompound.toFixed(),
        props.isMaxUnstakeMode,
      );
    } else {
      onChangeInput(internalValue.value);
    }
  },
);

// Update max amount
watch(
  async () => await calculateMaxAmount(),
  async maxAmountPromise => {
    maxAmount.value = await maxAmountPromise;
  },
  {
    immediate: true,
  },
);

return (_ctx: any,_cache: any) => {
  return (__props.pool)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass(["flex-column input-panel", { 'panel-focus': isPanelFocus.value, 'panel-error': isBalanceError.value }])
      }, [
        _createElementVNode("div", _hoisted_1, [
          (!_unref(state).layout.isMobile)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(_unref(t)(`yieldPool.${__props.action}`)), 1))
            : _createCommentVNode("", true),
          _createVNode(InputBalance, {
            "balance-value": getBalance().value,
            "balance-symbol": getBalance().symbol,
            "is-balance-error": isBalanceError.value,
            "is-max-disabled": _unref(eq)(getBalance().value, 0) || __props.disabled,
            onSetMax: onSetMax
          }, _createSlots({ _: 2 }, [
            (_unref(isBalanceTooltip))
              ? {
                  name: "tooltip",
                  fn: _withCtx(() => [
                    _createElementVNode("div", null, [
                      _createTextVNode(_toDisplayString(_unref(t)('wrappedContract.inChain', { chain: 'Cardano' })) + ": ", 1),
                      _createElementVNode("span", null, _toDisplayString(_unref(stakeBalanceInCardanoFixed)), 1)
                    ]),
                    _createElementVNode("div", null, [
                      _createTextVNode(_toDisplayString(_unref(t)('wrappedContract.inChain', { chain: 'Milkomeda' })) + ": ", 1),
                      _createElementVNode("span", null, _toDisplayString(_unref(stakeBalanceInMilkomedaFixed)), 1)
                    ])
                  ])
                }
              : undefined
          ]), 1032, ["balance-value", "balance-symbol", "is-balance-error", "is-max-disabled"])
        ]),
        _createElementVNode("div", _hoisted_3, [
          (!_unref(state).layout.isMobile)
            ? (_openBlock(), _createBlock(TokenIcon, {
                key: 0,
                "icon-src": _unref(poolToken)?.tokenIconUrl,
                symbol: _unref(poolToken)?.symbol,
                "as-label": "",
                full: ""
              }, null, 8, ["icon-src", "symbol"]))
            : _createCommentVNode("", true),
          _createVNode(BaseInputNumber, {
            modelValue: internalValue.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((internalValue).value = $event)),
            maxFractionDigits: _unref(maxFractionDigits),
            disabled: __props.disabled,
            onInput: _cache[1] || (_cache[1] = ($event: any) => (onChangeInput($event))),
            onFocus: _cache[2] || (_cache[2] = ($event: any) => (setPanelFocus(true))),
            onBlur: _cache[3] || (_cache[3] = ($event: any) => (setPanelFocus(false))),
            class: "flex-grow-1",
            "input-class": "input-xl ml-s3"
          }, null, 8, ["modelValue", "maxFractionDigits", "disabled"])
        ])
      ], 2))
    : _createCommentVNode("", true)
}
}

})